"use client"

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { AccordionItem } from "./Accordion"
import { Row, Col } from "./Grid"
import Aos from "aos"
import "aos/dist/aos.css"
import "flowbite"
import { Table } from "./Table"
import { PlanificazionePreview } from "./Preview"
import { CustomFileInput } from "./CustomFileInput/CustomFileInput"
import Banner from "./banner"
import { Edit, Trash2 } from "lucide-react"

// Importez les fichiers CSS Tailwind
import "../../../theme/tailwind/table.css"
import "../../../theme/tailwind/accordion.css"
import "../../../theme/tailwind/preview.css"
import "../../../theme/tailwind/buttons.css"
import "../../../theme/tailwind/typography.css"
import "../../../theme/tailwind/accordion-custom.css"

import { getPianificazioni, addPianificazione, updatePianificazione, deletePianificazione } from "../../../lib/api"
import { devError, devLog, devWarn } from "../../../utils/devConsole"
import { timezoneUtils } from "../../../utils/timezone"

export default function Service() {
  const navigate = useNavigate()
  const [plans, setPlans] = useState([])
  const [plansData, setPlansData] = useState({
    Nome: "",
    DataInizio: "",
    DataFine: "",
  })
  const [erroreNuovaPianificazione, setErroreNuovaPianificazione] = useState("")
  const [erroreVecchiaPianificazione, setErroreVecchiaPianificazione] = useState("")
  const [refreshKey, setRefreshKey] = useState(0)
  const [editingPlanId, setEditingPlanId] = useState(null)
  const [showNewPlanForm, setShowNewPlanForm] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)

  // Verifica dell'autenticazione rinforzata
  useEffect(() => {
    const checkAuthentication = () => {
      const token = localStorage.getItem("authToken")
      
      if (!token) {
        devWarn("🔒 [SERVICE] Nessun token trovato, reindirizzamento verso /signin")
        setIsAuthenticated(false)
        setIsCheckingAuth(false)
        navigate("/signin", { replace: true })
        return
      }

      // Verificare se il token è valido (formato JWT base)
      try {
        const tokenParts = token.split('.')
        if (tokenParts.length !== 3) {
          devWarn("🔒 [SERVICE] Token non valido, reindirizzamento verso /signin")
          localStorage.removeItem("authToken")
          localStorage.removeItem("authMethod")
          setIsAuthenticated(false)
          setIsCheckingAuth(false)
          navigate("/signin", { replace: true })
          return
        }

        // Token valido, autorizzare l'accesso
        devLog("✅ [SERVICE] Token valido, accesso autorizzato")
        setIsAuthenticated(true)
        setIsCheckingAuth(false)
        fetchPlans()
      } catch (error) {
        devError("🔒 [SERVICE] Errore durante la verifica del token:", error)
        localStorage.removeItem("authToken")
        localStorage.removeItem("authMethod")
        setIsAuthenticated(false)
        setIsCheckingAuth(false)
        navigate("/signin", { replace: true })
      }
    }

    Aos.init({ duration: 1000 })
    checkAuthentication()
  }, [navigate])

  // Ascoltare i cambiamenti di localStorage per rilevare la disconnessione
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'authToken' && !e.newValue) {
        devLog("🔒 [SERVICE] Token rimosso, reindirizzamento verso /signin")
        setIsAuthenticated(false)
        navigate("/signin", { replace: true })
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [navigate])

  const fetchPlans = async () => {
    try {
      const data = await getPianificazioni()
      setPlans(data)
    } catch (error) {
      devError("Error fetching pianificazioni:", error)
    }
  }

  const saveNewPlan = async (e) => {
    e.preventDefault()
    
    // Validazione lato client migliorata
    if (!plansData.Nome || !plansData.DataInizio || !plansData.DataFine) {
      setErroreNuovaPianificazione("Tutti i campi sono obbligatori.")
      return
    }

    // Validazione della lunghezza del nome
    if (plansData.Nome.length > 255) {
      setErroreNuovaPianificazione("Il nome non può superare i 255 caratteri.")
      return
    }

    // Validazione delle date
    const startDate = new Date(plansData.DataInizio)
    const endDate = new Date(plansData.DataFine)
    
    if (endDate < startDate) {
      setErroreNuovaPianificazione("La data di fine non può essere precedente alla data di inizio.")
      return
    }

    try {
      await addPianificazione(plansData)
      setPlansData({ Nome: "", DataInizio: "", DataFine: "" })
      setShowNewPlanForm(false)
      setErroreNuovaPianificazione("")
      fetchPlans()
    } catch (error) {
      devError("Error adding pianificazione:", error)
      
      // Gestione degli errori specifici
      if (error.message.includes("cannot exceed 255 characters")) {
        setErroreNuovaPianificazione("Il nome non può superare i 255 caratteri.")
      } else if (error.message.includes("cannot be empty")) {
        setErroreNuovaPianificazione("Il nome non può essere vuoto.")
      } else {
        setErroreNuovaPianificazione("Errore durante l'aggiunta della pianificazione.")
      }
    }
  }

  const handleNewPlan = (e) => {
    setPlansData({ ...plansData, [e.target.name]: e.target.value })
  }

  const undoNewPlan = () => {
    setPlansData({ Nome: "", DataInizio: "", DataFine: "" })
    setShowNewPlanForm(false)
    setErroreNuovaPianificazione("")
  }

  const addNewPlan = () => {
    setShowNewPlanForm(true)
    setPlansData({ Nome: "", DataInizio: "", DataFine: "" })
  }

  const deletePlan = async (id) => {
    try {
      await deletePianificazione(id)
      fetchPlans()
    } catch (error) {
      devError("Error deleting pianificazione:", error)
    }
  }

  const editPlan = (plan) => {
    setPlansData({
      Nome: plan.Nome,
      DataInizio: plan.DataInizio.split("T")[0],
      DataFine: plan.DataFine.split("T")[0],
    })
    setEditingPlanId(plan.pianificazione_id)
  }

  const undoEditPlan = () => {
    setPlansData({ Nome: "", DataInizio: "", DataFine: "" })
    setEditingPlanId(null)
    setErroreVecchiaPianificazione("")
  }

  const saveEditPlan = async (e) => {
    e.preventDefault()

    // Validazione lato client migliorata
    if (!plansData.Nome || !plansData.DataInizio || !plansData.DataFine) {
      setErroreVecchiaPianificazione("Tutti i campi sono obbligatori.")
      return
    }

    // Validazione della lunghezza del nome
    if (plansData.Nome.length > 255) {
      setErroreVecchiaPianificazione("Il nome non può superare i 255 caratteri.")
      return
    }

    // Validazione delle date
    const startDate = new Date(plansData.DataInizio)
    const endDate = new Date(plansData.DataFine)
    
    if (endDate < startDate) {
      setErroreVecchiaPianificazione("La data di fine non può essere precedente alla data di inizio.")
      return
    }

    try {
      await updatePianificazione(editingPlanId, plansData)
      undoEditPlan()
      fetchPlans()
    } catch (error) {
      devError("Error updating pianificazione:", error)
      
      // Gestione degli errori specifici
      if (error.message.includes("cannot exceed 255 characters")) {
        setErroreVecchiaPianificazione("Il nome non può superare i 255 caratteri.")
      } else if (error.message.includes("cannot be empty")) {
        setErroreVecchiaPianificazione("Il nome non può essere vuoto.")
      } else {
        setErroreVecchiaPianificazione("Errore durante l'aggiornamento della pianificazione.")
      }
    }
  }

  // Mostrare un caricamento durante la verifica dell'autenticazione
  if (isCheckingAuth) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#dc3545] mx-auto mb-4"></div>
          <p className="text-gray-600">Verifica autenticazione...</p>
        </div>
      </div>
    )
  }

  // Se l'utente non è autenticato, non mostrare nulla (reindirizzamento in corso)
  if (!isAuthenticated) {
    return null
  }

  return (
    <div className="bg-white">
      <div className="banner-background bg-[url('../../assets/Images/Services/ServicesBanner.png')]">
        <div className="text-center" data-aos="fade-down">
          <h6 className="title-area-riservata">AREA RISERVATA</h6>
        </div>
      </div>

      <div className="section-spacing">
        <Banner />
      </div>

      <div className="container mx-auto px-1 sm:px-4 section-spacing-sm">
        <div className="text-center mb-6">
          <h1 className="title-section">Pianificazioni</h1>
        </div>

        <div className="mt-6">
          {plans.length > 0 && (
            <Row>
              <Col xl={12} lg={12} md={12} sm={12} xs={12}>
                <div className="space-y-6">
                  {plans.map((plan, index) => (
                    <AccordionItem
                      key={plan.pianificazione_id}
                      className="accordion-item"
                      title={(isActive) => !isActive && <h2 className="title-accordion">{plan.Nome}</h2>}
                    >
                      <div className="px-0 sm:px-4">
                        {editingPlanId === plan.pianificazione_id ? (
                          <>
                            <h3 className="text-xl font-medium mb-6 text-[#dc3545] text-center">
                              Modifica pianificazione
                            </h3>

                            <form onSubmit={saveEditPlan} className="text-center mb-8">
                              <input
                                className="w-full sm:w-1/2 border border-gray-300 rounded px-3 py-2 text-center mb-6 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 placeholder:text-center"
                                name="Nome"
                                type="text"
                                value={plansData.Nome}
                                onChange={handleNewPlan}
                                placeholder="Nome pianificazione"
                              />

                              <div className="flex flex-nowrap items-center justify-center gap-0.5 mb-4">
                                <span className="add-plan-date-label text-sm">dal</span>
                                <input
                                  className="border border-gray-300 rounded px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-[140px] sm:w-[140px] text-sm placeholder:text-gray-400 placeholder:italic"
                                  name="DataInizio"
                                  type="date"
                                  value={plansData.DataInizio}
                                  onChange={handleNewPlan}
                                  placeholder="gg/mm/aaaa"
                                  pattern="\\d{4}-\\d{2}-\\d{2}"
                                />
                                <span className="add-plan-date-label text-sm">al</span>
                                <input
                                  className="border border-gray-300 rounded px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-[140px] sm:w-[140px] text-sm placeholder:text-gray-400 placeholder:italic"
                                  name="DataFine"
                                  type="date"
                                  value={plansData.DataFine}
                                  onChange={handleNewPlan}
                                  placeholder="gg/mm/aaaa"
                                  pattern="\\d{4}-\\d{2}-\\d{2}"
                                />
                                <span className="add-plan-date-label text-sm"></span>
                              </div>

                              {erroreVecchiaPianificazione && (
                                <div className="text-red-600 text-sm mt-1 mb-4 text-center">
                                  {erroreVecchiaPianificazione}
                                </div>
                              )}

                              <div className="flex justify-center space-x-4">
                                <button
                                  type="button"
                                  className="px-5 py-3 bg-[#dc3545] text-white rounded transition-colors duration-300 hover:bg-[#c82333] min-w-[100px]"
                                  onClick={undoEditPlan}
                                >
                                  Annulla
                                </button>
                                <button
                                  type="submit"
                                  className="px-5 py-3 bg-[#dc3545] text-white rounded transition-colors duration-300 hover:bg-[#c82333] min-w-[100px]"
                                >
                                  Salva
                                </button>
                              </div>
                            </form>
                          </>
                        ) : (
                          <>
                            <h2 className="title-plan text-center" id={"NomePianificazione" + plan.Numero}>
                              {plan.Nome}
                            </h2>

                            <p className="banner-dates text-center font-semibold" id={"DatePianificazione" + plan.Numero}>
                              (dal {timezoneUtils.formatItalianDateShort(plan.DataInizio)} al{" "}
                              {timezoneUtils.formatItalianDateShort(plan.DataFine)})
                            </p>

                            <div className="flex justify-center space-x-4 mb-6">
                              <button
                                className="button-custom mx-1"
                                title="ModificaPianificazione"
                                type="button"
                                onClick={() => editPlan(plan)}
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              <button
                                className="button-custom mx-1"
                                title="EliminaPianificazione"
                                type="button"
                                onClick={() => deletePlan(plan.pianificazione_id)}
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </>
                        )}

                        <PlanificazionePreview numP={plan.pianificazione_id} refreshKey={refreshKey} />
                        <div className="mb-6"></div>
                        <Table
                          numP={plan.pianificazione_id}
                          refreshKey={refreshKey}
                          entityType="pianificazione"
                          onDataUpdated={() => setRefreshKey((prev) => prev + 1)}
                        />
                        <CustomFileInput
                          numP={plan.pianificazione_id}
                          entityType="pianificazione"
                          onImageAdded={() => {
                            setRefreshKey((prev) => prev + 1)
                            setTimeout(() => window.dispatchEvent(new Event("resize")), 100)
                          }}
                        />
                      </div>
                    </AccordionItem>
                  ))}
                </div>
              </Col>
            </Row>
          )}

          {showNewPlanForm && (
            <div className="add-plan-form my-8">
              <form onSubmit={saveNewPlan} className="flex flex-col items-center">
                <div className="add-plan-title mb-4">
                  <h3 className="text-xl sm:text-2xl md:text-3xl text-[#dc3545] text-center mb-6">Nuova pianificazione</h3>
                </div>

                <input
                  className="w-full sm:w-1/2 border border-gray-300 rounded px-3 py-2 text-center mb-6 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 placeholder:text-center"
                  name="Nome"
                  type="text"
                  value={plansData.Nome}
                  onChange={handleNewPlan}
                  placeholder="Nome pianificazione"
                />

                <div className="flex flex-nowrap items-center justify-center gap-0.5 mb-4">
                  <span className="add-plan-date-label text-sm">dal</span>
                  <input
                    className="border border-gray-300 rounded px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-[140px] sm:w-[140px] text-sm placeholder:text-gray-400 placeholder:italic"
                    name="DataInizio"
                    type="date"
                    value={plansData.DataInizio}
                    onChange={handleNewPlan}
                    placeholder="gg/mm/aaaa"
                    pattern="\\d{4}-\\d{2}-\\d{2}"
                  />
                  <span className="add-plan-date-label text-sm">al</span>
                  <input
                    className="border border-gray-300 rounded px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-[140px] sm:w-[140px] text-sm placeholder:text-gray-400 placeholder:italic"
                    name="DataFine"
                    type="date"
                    value={plansData.DataFine}
                    onChange={handleNewPlan}
                    placeholder="gg/mm/aaaa"
                    pattern="\\d{4}-\\d{2}-\\d{2}"
                  />
                  <span className="add-plan-date-label text-sm"></span>
                </div>

                {erroreNuovaPianificazione && (
                  <div className="form-error mb-4 text-center">{erroreNuovaPianificazione}</div>
                )}

                <div className="add-plan-actions flex gap-2 justify-center mt-4">
                  <button type="button" className="px-5 py-3 bg-[#dc3545] text-white rounded transition-colors duration-300 hover:bg-[#c82333] min-w-[100px] w-full sm:w-auto" onClick={undoNewPlan}>
                    Annulla
                  </button>
                  <button type="submit" className="px-5 py-3 bg-[#dc3545] text-white rounded transition-colors duration-300 hover:bg-[#c82333] min-w-[100px] w-full sm:w-auto">
                    Salva
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="mt-8 mb-16 text-center">
            <button title="AggiungiPianificazione" className="button-custom w-full py-3 px-6" onClick={addNewPlan}>
              Aggiungi pianificazione
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
